'use client';

import React from 'react';
import { tv } from 'tailwind-variants';
import type {
  FormValues,
  FormErrors,
  FormTouched,
  StepConfig,
  FieldConfig,
  FormInstance,
} from '../../types';
import { FieldGroup } from '../WillsFormStep/FieldGroup';
import { InlineCardList } from '../InlineCard/InlineCardList';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import type { UseAddMoreFormReturn } from '../../hooks/useAddMoreForm';
import { EstateProgressBar } from '../EstateProgressBar/EstateProgressBar';
import { useUIText } from '../../hooks/useUIText';

interface AddMoreFormProps {
  step: StepConfig;
  values: FormValues;
  errors: FormErrors;
  touched: FormTouched;
  addMoreForm: UseAddMoreFormReturn;
  handleChange: (field: FieldConfig, rawValue: unknown) => void;
  setTouched: React.Dispatch<React.SetStateAction<FormTouched>>;
  onAdd: () => void;
  onAddAnother: () => void;
  onEditInstance?: (instanceId: string) => void;
  stepsById: Record<string, StepConfig>;
  isSubmitting: boolean;
  canAddMore: boolean;
  fieldGroupIndex: number; // Index of the fieldGroup to render
  cardTitle?: string; // Title for inline cards (e.g., "Child", "Guardian")
  completedForms?: Record<string, FormInstance[]>; // Completed form instances for progress calculation
  localStorageKey?: string; // Optional localStorage key to load estate values from
}

/**
 * Generic component for rendering "Add More" form fields and inline cards
 * Can be used for children, guardian, or any other multi-instance form
 */
export const AddMoreForm: React.FC<AddMoreFormProps> = ({
  step,
  values,
  errors,
  touched,
  addMoreForm,
  handleChange,
  setTouched,
  onAdd,
  onAddAnother,
  onEditInstance,
  isSubmitting,
  canAddMore,
  fieldGroupIndex,
  cardTitle = 'Item',
  completedForms = {},
  localStorageKey,
}) => {
  const { getButtonText } = useUIText();

  // Get step config for inline cards
  const cardSteps = React.useMemo(
    () =>
      step.fieldGroups && step.fieldGroups[fieldGroupIndex]
        ? [
            {
              id: step.id,
              title: step.title,
              fieldGroups: [step.fieldGroups[fieldGroupIndex]],
            },
          ]
        : [],
    [step.id, step.title, step.fieldGroups, fieldGroupIndex]
  );

  // Find the fieldGroup
  const fieldGroup = step.fieldGroups?.[fieldGroupIndex];
  const addMoreConfig = step.addMoreConfig;

  // Get current form values for progress bar calculation (values being entered but not yet saved)
  const currentFormValues = React.useMemo(() => {
    const currentValues: FormValues = {};
    if (addMoreConfig?.fieldIds) {
      addMoreConfig.fieldIds.forEach((fieldId) => {
        if (values[fieldId] !== undefined) {
          currentValues[fieldId] = values[fieldId];
        }
      });
    }
    return currentValues;
  }, [values, addMoreConfig]);

  // Early return AFTER all hooks
  if (!fieldGroup || !addMoreConfig) {
    return null;
  }

  const displayTitleFields = addMoreConfig.displayTitleFields;

  const { container, inlineCardsWrapper, addButtonWrapper, addAnotherButtonWrapper } =
    TAILWIND_VARIANTS();

  return (
    <div className={container()}>
      {/* Estate/Cash Progress Bar - show above fields if configured */}
      {addMoreConfig.showProgressBar && (
        <EstateProgressBar
          values={values}
          completedForms={completedForms}
          addMoreConfig={addMoreConfig}
          currentFormValues={currentFormValues}
          localStorageKey={localStorageKey}
          editingInstanceId={addMoreForm.editingInstanceId}
        />
      )}
      {/* Inline cards for completed instances - render before form fields */}
      {addMoreForm.instances.length > 0 && (
        <div className={inlineCardsWrapper()}>
          <InlineCardList
            instances={addMoreForm.instances}
            steps={cardSteps}
            title={cardTitle}
            onEdit={(instanceId) => {
              if (onEditInstance) {
                onEditInstance(instanceId);
                addMoreForm.setShowForm(true);
              }
            }}
            editingInstanceId={addMoreForm.editingInstanceId}
            fieldGroup={fieldGroup}
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            setTouched={setTouched}
            onUpdate={onAdd} // Pass onAdd as onUpdate handler
            isSubmitting={isSubmitting}
            canAddMore={canAddMore}
            displayTitleFields={displayTitleFields}
          />
        </div>
      )}

      {/* Render form fields - only show if NOT editing an existing instance */}
      {addMoreForm.isFormVisible && !addMoreForm.editingInstanceId && (
        <FieldGroup
          fieldGroup={fieldGroup}
          values={values}
          errors={errors}
          touched={touched}
          handleChange={handleChange}
          setTouched={setTouched}
        />
      )}

      {/* "Add" button - appears when form is visible and not editing */}
      {step.addMore &&
        canAddMore &&
        addMoreForm.isFormVisible &&
        !addMoreForm.editingInstanceId && (
          <div className={addButtonWrapper()}>
            <ButtonWrapper
              type="button"
              text={getButtonText('add', 'Add')}
              ctaComponentClass="default"
              ctaVariant="primary"
              onClick={onAdd}
              disabled={isSubmitting}
              remixIcon="add-line"
              remixIconSize="sm"
              ctaIconAlignment="right"
              className="leading-[100%] md:leading-[100%] lg:leading-[100%] h-12"
            />
          </div>
        )}

      {/* "Add another" button - appears when form is not visible */}
      {/* Hide for guardian step when no instances (use custom CTA instead) */}
      {step.addMore &&
        canAddMore &&
        !addMoreForm.isFormVisible &&
        !(
          (step.id === 'step-4-6-guardian' || step.id === 'step-5-5-guardian') &&
          addMoreForm.instances.length === 0
        ) && (
          <div className={addAnotherButtonWrapper()}>
            <ButtonWrapper
              type="button"
              text={getButtonText('addAnother', 'Add another')}
              ctaComponentClass="default"
              ctaVariant="primary"
              onClick={onAddAnother}
              disabled={isSubmitting}
              remixIcon="add-line"
              remixIconSize="sm"
              ctaIconAlignment="right"
              className="leading-[100%] md:leading-[100%] lg:leading-[100%] h-12"
            />
          </div>
        )}
    </div>
  );
};

const TAILWIND_VARIANTS = tv({
  slots: {
    container: [
      'sub-section-container',
      'flex',
      'flex-col',
      'justify-start',
      'gap-spacing-spacing-24',
    ],
    inlineCardsWrapper: ['w-full'],
    addButtonWrapper: ['w-auto'],
    addAnotherButtonWrapper: ['w-auto'],
  },
});
